import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Menu, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { getBousalaResumeState } from "@/lib/bousala-purchase.functions";
import { performSignOut } from "@/lib/auth/sign-out";
import logoWide from "@/assets/drsara-logo-maroon.png.asset.json";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SITE_NAV, isNavActive } from "@/components/public/site-nav";
import { MobileMenuSheet } from "@/components/public/mobile-menu-sheet";

const primarySmall =
  "inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-ds-pill bg-primary px-5 font-display text-[15px] font-bold text-primary-foreground shadow-accent transition-colors duration-ds-fast ease-ds hover:bg-primary-hover";
const ghost =
  "inline-flex min-h-11 items-center gap-2 rounded-ds-pill px-4 font-display text-[15px] font-bold text-brown-700 transition-colors duration-ds-fast ease-ds hover:bg-brown-700/5";

type SiteHeaderProps = {
  /** Header ground: "article" matches the cream-50 article page. */
  tone?: "default" | "article";
  /** Replaces the end actions (desktop and mobile). Logo and desktop nav stay. */
  endSlot?: ReactNode;
};

export function SiteHeader({ endSlot, tone = "default" }: SiteHeaderProps = {}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [, setIsAnon] = useState(false);
  const [prefLang, setPrefLang] = useState<"ar" | "en" | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    // getUser() re-validates the session with Supabase Auth; getSession()
    // returns cached/stale data and can lie about identity.
    supabase.auth.getUser().then(({ data }) => {
      setEmail(data.user?.email ?? null);
      setUserId(data.user?.id ?? null);
      setIsAnon(Boolean(data.user) && !data.user?.email);
    });
    const { data } = supabase.auth.onAuthStateChange((_e, session) => {
      setEmail(session?.user.email ?? null);
      setUserId(session?.user.id ?? null);
      setIsAnon(Boolean(session?.user) && !session?.user.email);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    try {
      const v = window.localStorage.getItem("bousala.lang");
      if (v === "ar" || v === "en") setPrefLang(v);
    } catch {}
    const onStorage = (e: StorageEvent) => {
      if (e.key === "bousala.lang" && (e.newValue === "ar" || e.newValue === "en")) setPrefLang(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const langSearch: { lang?: "ar" | "en" } = prefLang ? { lang: prefLang } : {};

  async function signOut() {
    await performSignOut(qc);
    navigate({ to: "/", replace: true });
  }

  const hideResumePill =
    pathname.startsWith("/bousala/play") ||
    pathname.startsWith("/portal/bousala") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/auth");
  const { data: resume } = useQuery({
    queryKey: ["bousala", "resume-state", userId],
    enabled: Boolean(userId) && !hideResumePill,
    queryFn: () => getBousalaResumeState(),
    staleTime: 30_000,
  });
  const showResume =
    !hideResumePill &&
    resume?.access === "granted" &&
    resume.attemptStatus === "in_progress" &&
    Boolean(resume.attemptId);

  return (
    <header className={`sticky top-0 z-40 ${tone === "article" ? "bg-cream-100/90" : "bg-cream-200/[.86]"} backdrop-blur-[14px] lg:border-b lg:border-hairline-subtle`}>
      <div className="mx-auto flex w-[min(100%-2.5rem,76rem)] items-center justify-between gap-4 py-3 lg:h-[76px] lg:py-0">
        <Link to="/" className="flex min-h-11 shrink-0 items-center py-1" aria-label="د. سارة النحّاس — الرئيسية">
          <img src={logoWide.url} alt="د. سارة النحّاس" width={1000} height={460} className="h-9 w-auto lg:h-[38px]" />
        </Link>

        <nav aria-label="القائمة الرئيسية" className="hidden items-center gap-1 lg:flex">
          {SITE_NAV.map((n) => {
            const active = isNavActive(n.to, pathname);
            return (
              <Link
                key={n.to}
                to={n.to as any}
                aria-current={active ? "page" : undefined}
                className={`inline-flex min-h-11 items-center rounded-ds-pill px-4 font-display text-[17px] transition-colors duration-ds-fast ease-ds ${
                  active ? "bg-surface font-bold text-brown-700" : "font-medium text-brown-500 hover:bg-brown-700/5"
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        {endSlot !== undefined && (
          <div className="flex items-center gap-2 lg:hidden">{endSlot}</div>
        )}
        {/* Desktop: the quiet account link stays; a page's end slot replaces only the CTA. */}
            <div className="hidden items-center gap-1 lg:flex">
              {userId ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={email ? `حسابي: ${email}` : "حسابي"}
                      className={`${ghost} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                    >
                      <span className="max-w-[140px] truncate">{email ?? "حسابي"}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={8} className="w-48 rounded-ds-md border border-hairline-subtle bg-surface shadow-md">
                    <DropdownMenuItem asChild>
                      <Link to="/portal" className="cursor-pointer text-sm font-semibold text-foreground">
                        لوحتي
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => { void signOut(); }} className="cursor-pointer gap-2 text-sm font-semibold text-foreground">
                      <LogOut className="h-4 w-4" strokeWidth={1.75} />
                      تسجيل الخروج
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/auth" search={{ mode: "sign_in" } as any} className={ghost}>
                  تسجيل الدخول
                </Link>
              )}
              {showResume && (
                <Link to="/bousala/play" search={langSearch as any} className={ghost}>
                  إكمال البوصلة
                </Link>
              )}
              {endSlot !== undefined ? (
                <div className="ms-1 flex items-center gap-2">{endSlot}</div>
              ) : (
                <Link to="/book" className={`${primarySmall} ms-1`}>
                  حجز استشارة
                </Link>
              )}
            </div>

        {endSlot === undefined && (
          <>
            <div className="flex items-center gap-2 lg:hidden">
              {pathname !== "/" && (
                <Link to="/book" className={primarySmall}>
                  حجز استشارة
                </Link>
              )}
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="grid h-12 w-12 place-items-center rounded-full border border-hairline-default bg-surface/60 text-brown-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="القائمة"
                aria-expanded={open}
                aria-controls="site-header-mobile-nav"
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileMenuSheet
        open={open}
        onOpenChange={setOpen}
        pathname={pathname}
        signedIn={Boolean(userId)}
        onSignOut={() => { setOpen(false); void signOut(); }}
        showResume={showResume}
        langSearch={langSearch}
      />
    </header>
  );
}
